import { useCallback, useEffect, useRef, useState } from 'react';
import { LevelMeter } from '../components/LevelMeter';
import { CamPreview } from '../components/CamPreview';
import { CamTile } from '../components/CamTile';
import { InputDeviceMeter } from '../components/InputDeviceMeter';
import { CustomerPage, CardTitle, Field, TextInput, Select, Pill, Hint, Row } from '../components/live/CustomerPage';
import { LiveShell, ColLabel } from '../components/live/LiveShell';
import { VideoStage } from '../components/live/VideoStage';
import { PermissionNotice } from '../components/live/PermissionNotice';
import { useMediaSetup } from '../hooks/useMediaSetup';
import { startEncoder, type EncoderHandle } from '../lib/audio/encoder-loader';
import { fetchIceServers } from '../lib/turn';
import { createL16DataChannel, createPeer, preferOpusStereo } from '../lib/webrtc';
import { SignalingClient } from '../lib/signaling-client';
import { decodePacket, PACKET_PAYLOAD_SAMPLES } from '../lib/packet';
import { pushRing, type RingEntry } from '../lib/audio/pre-roll-ring';
import { createOpfsRecorder, readOpfsRaw, type RecorderHandle } from '../lib/audio/opfs-recorder';
import { startReturnPlayback, type ReturnPlaybackHandle } from '../lib/audio/return-playback';
import { startKeepAlive } from '../lib/audio/keepalive';
import { GIVEUP_WINDOW_MS } from '../../shared/reconnect';
import { SpeakerConference } from '../lib/conference/conference-session';

// Pre-Roll: ~1s lokales Sprecher-Audio VOR dem Take-Trigger (docs .../2026-06-11-take-preroll-design.md).
const PRE_ROLL_MS = 1000;
const PRE_ROLL_PACKETS = Math.ceil((48000 * PRE_ROLL_MS) / 1000 / PACKET_PAYLOAD_SAMPLES); // = 94 (Math.ceil deckt krumme Sample-Raten/Längen ab)

type Status = 'idle' | 'waiting' | 'connecting' | 'connected' | 'reconnecting' | 'failed' | 'lost' | 'kicked' | 'roomfull';

export function Speaker({ slug }: { slug: string }) {
  const sessionId = `studio-${slug}`;
  const [micId, setMicId] = useState(() => localStorage.getItem(`tro-spk-mic:${slug}`) ?? '');
  const [status, setStatus] = useState<Status>('idle');
  const [packetsSent, setPacketsSent] = useState(0);
  const [headphoneId, setHeadphoneId] = useState(() => localStorage.getItem(`tro-spk-out:${slug}`) ?? '');
  const [hasReturn, setHasReturn] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [name, setName] = useState('');
  const encoderRef = useRef<EncoderHandle | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const sigRef = useRef<SignalingClient | null>(null);
  const lastSamplesRef = useRef<Int16Array | null>(null);
  const recorderRef = useRef<RecorderHandle | null>(null);
  const pendingRecorderRef = useRef<RecorderHandle | null>(null); // wartet auf Seq-0-Grenze
  const playbackRef = useRef<ReturnPlaybackHandle | null>(null);
  const giveUpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCandsRef = useRef<RTCIceCandidateInit[]>([]); // ICE vor setRemoteDescription puffern
  const rebuildingRef = useRef(false); // Überlapp-Schutz: nie zwei buildPeer parallel
  const keepAliveRef = useRef<(() => void) | null>(null); // Tab-Keepalive (gegen Drosselung)
  const lastSeqRef = useRef<number>(-1);
  const takeRecRef = useRef<RecorderHandle | null>(null);
  // takeId → der seq des ERSTEN Samples im Slice (für die Conform-Ausrichtung, auch beim Retry).
  const takeStartSeqRef = useRef<Map<string, number>>(new Map());
  const activeTakeRef = useRef<{ takeId: string; uploadKey: string; uploadToken: string; gotFirst: boolean } | null>(null);
  // Rollender ~1s-Ring der letzten Sprecher-Pakete (für den Take-Pre-Roll). Läuft, sobald der Encoder läuft.
  const preRollRef = useRef<RingEntry[]>([]);
  // Video-Konferenz (SP3b): EIGENER Strang (separater WS + SFU-PC), best-effort. Ein Fehler hier
  // darf den bit-exakten L16-Pfad NIE stören.
  const [camId, setCamId] = useState(() => localStorage.getItem(`tro-spk-cam:${slug}`) ?? '');   // "" = No Video (aus); sonst Kamera-Device-ID
  // Geräte + Berechtigung (Mikro + Kamera) am Setup-Screen — siehe useMediaSetup.
  const media = useMediaSetup(status === 'idle');
  const { inputs, outputs, cams } = media;
  const speakerConfRef = useRef<SpeakerConference | null>(null);
  const [hostVideoTrack, setHostVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [peerVideos, setPeerVideos] = useState<Map<string, MediaStreamTrack>>(new Map());
  const [selfCamTrack, setSelfCamTrack] = useState<MediaStreamTrack | null>(null);   // eigenes Kamerabild
  const [confGuests, setConfGuests] = useState<{ guestId: string; name: string }[]>([]);
  const [confSpeakers, setConfSpeakers] = useState<{ speakerId: string; name: string }[]>([]);

  const uploadTakeSlice = useCallback(async (pcm: ArrayBuffer, key: string, token: string): Promise<boolean> => {
    try {
      const res = await fetch(`/api/takes/upload?key=${encodeURIComponent(key)}`, {
        method: 'PUT', headers: { 'x-upload-token': token, 'content-type': 'application/octet-stream' }, body: pcm,
      });
      return res.ok;
    } catch { return false; }
  }, []);

  // Video-Konferenz-Strang starten (best-effort). Holt den Konf-Token des aktiven Kundenraums;
  // ohne aktiven Raum (kein Token) passiert nichts. Eigener WS + SFU-PC → L16 bleibt unberührt.
  const startSpeakerConference = useCallback(async () => {
    if (speakerConfRef.current) return;
    const r = await fetch(`/api/studio/${encodeURIComponent(slug)}/conference`).then((x) => x.json()).catch(() => null);
    if (!(r as { slug?: string } | null)?.slug) return;   // kein live Host → kein Video
    const conf = new SpeakerConference(slug, sessionId, name || 'Speaker', { cameraDeviceId: camId || undefined }, {
      onHostVideo: (track) => setHostVideoTrack(track),
      onPeerVideo: (key, track) => setPeerVideos((prev) => { const next = new Map(prev); if (track) next.set(key, track); else next.delete(key); return next; }),
      onLocalVideo: (track) => setSelfCamTrack(track),
      onPeers: (guests, speakers) => {
        setConfGuests(guests.map((g) => ({ guestId: g.guestId, name: g.name })));
        setConfSpeakers(speakers.map((s) => ({ speakerId: s.speakerId, name: s.name })));
      },
    });
    speakerConfRef.current = conf;
    try { await conf.join(); } catch { speakerConfRef.current = null; }
  }, [slug, sessionId, name, camId]);

  // Baut die per-Admit-Media ab (PC/DataChannel/Playback/Recorder) und exportiert das
  // Backup. Der ENCODER (Mic-Capture) bleibt absichtlich am Leben — er läuft ab dem
  // Connect-Klick über Warteraum + Reconnects + Dismiss durch (Pegel + Vorwärmung); erst
  // ein echter Austritt (stop/giveUp/onKicked) stoppt ihn via stopMic().
  const teardownMedia = useCallback(() => {
    dcRef.current?.close();
    pcRef.current?.close();
    playbackRef.current?.stop();
    playbackRef.current = null;
    setHasReturn(false);
    // ein noch nicht aktivierter pending-Recorder (Seq-0 noch nicht gekommen) verwerfen
    void pendingRecorderRef.current?.close();
    pendingRecorderRef.current = null;
    const rec = recorderRef.current;
    dcRef.current = null; pcRef.current = null; recorderRef.current = null;
    if (rec) {
      rec.exportWav()
        .then((blob) => setDownloadUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); }))
        .catch((e) => console.error('recording export failed', e));
    }
  }, []);

  // Mic-Encoder starten — schon beim Connect-Klick (User-Geste, Tab garantiert aktiv),
  // damit der schwere AudioWorklet-/getUserMedia-Aufbau NICHT im gedrosselten Hintergrund
  // passiert. Läuft über Warteraum + Reconnects durch. Der Pegel (lastSamplesRef) läuft ab
  // sofort; Aufnahme (recorderRef) + Senden (dcRef) hängen sich erst an, sobald gesetzt.
  // Die Aufnahme beginnt exakt am Seq-0-Paket (Encoder wird bei goLive resettet), damit das
  // Backup bei Seq 0 startet (Conform-Ausrichtung).
  const startMic = useCallback(async () => {
    if (encoderRef.current) return; // läuft schon
    encoderRef.current = await startEncoder({
      deviceId: micId,
      onPacket: (pkt) => {
        const { seq, samples } = decodePacket(pkt);
        lastSeqRef.current = seq;
        lastSamplesRef.current = samples;
        // samples.slice(): der Worklet-Buffer wird wiederverwendet → kopieren, bevor er in den Ring geht.
        pushRing(preRollRef.current, { seq, samples: samples.slice() }, PRE_ROLL_PACKETS);
        if (seq === 0 && pendingRecorderRef.current) {
          recorderRef.current = pendingRecorderRef.current;
          pendingRecorderRef.current = null;
        }
        const dc = dcRef.current;
        if (dc && dc.readyState === 'open') { dc.send(pkt); setPacketsSent((n) => n + 1); }
        if (recorderRef.current) void recorderRef.current.write(samples);
        const at = activeTakeRef.current;
        if (at && takeRecRef.current) {
          if (!at.gotFirst) { at.gotFirst = true; takeStartSeqRef.current.set(at.takeId, seq); }
          void takeRecRef.current.write(samples);
        }
      },
    });
  }, [micId]);

  const stopMic = useCallback(() => {
    encoderRef.current?.stop();
    encoderRef.current = null;
    lastSamplesRef.current = null;
  }, []);

  const clearGiveUp = useCallback(() => {
    if (giveUpTimerRef.current) { clearTimeout(giveUpTimerRef.current); giveUpTimerRef.current = null; }
  }, []);

  // Lokales Give-up: Recorder finalisieren (Take sichern), DO bewusst verlassen, → lost.
  const giveUp = useCallback(() => {
    clearGiveUp();
    keepAliveRef.current?.(); keepAliveRef.current = null;
    stopMic();
    teardownMedia();
    sigRef.current?.setLive(false);
    sigRef.current?.close();
    sigRef.current = null;
    speakerConfRef.current?.teardown(); speakerConfRef.current = null; // Video-Strang (separat von L16)
    setStatus('lost');
  }, [clearGiveUp, teardownMedia, stopMic]);

  // Baut eine FRISCHE PeerConnection + DataChannel auf (neues DTLS) und schickt ein
  // frisches Offer. Encoder/Recorder bleiben unberührt → durchgehende Aufnahme.
  // Wird sowohl beim ersten Connect (goLive) als auch beim Reconnect (onResumed) genutzt.
  // Grund für Voll-Neuaufbau statt ICE-Restart: nach einem LAN-Ausfall erreicht die PC
  // `failed`; der DTLS-Transport ist dann terminal und heilt per ICE-Restart NICHT
  // (ICE reconnectet, connectionState bleibt failed → DataChannel tot).
  const doBuildPeer = useCallback(async () => {
    const sig = sigRef.current;
    if (!sig) return;
    dcRef.current?.close();
    pcRef.current?.close();
    pcRef.current = null;
    pendingCandsRef.current = []; // frische PC → alten Candidate-Puffer verwerfen
    const iceServers = await fetchIceServers();
    const pc = await createPeer(iceServers);
    pcRef.current = pc;
    const dc = createL16DataChannel(pc);
    dcRef.current = dc;
    dc.binaryType = 'arraybuffer';
    pc.addTransceiver('audio', { direction: 'recvonly' });
    pc.ontrack = (e) => {
      playbackRef.current?.stop();
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      playbackRef.current = startReturnPlayback(stream, headphoneId || undefined);
      setHasReturn(true);
    };
    pc.onicecandidate = (e) => { if (e.candidate) { sig.send({ type: 'ICE', candidate: e.candidate.toJSON() }); } };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === 'connected') {
        clearGiveUp();
        setStatus('connected');
      } else if (st === 'disconnected' || st === 'failed') {
        if (giveUpTimerRef.current === null) {
          giveUpTimerRef.current = setTimeout(giveUp, GIVEUP_WINDOW_MS);
        }
        setStatus('reconnecting');
        // Frische Signaling-Leitung erzwingen (WS evtl. tot-aber-OPEN) → DO antwortet
        // RESUMED → onResumed baut die PC komplett neu.
        sigRef.current?.reconnectNow();
      }
    };
    const offer = await pc.createOffer();
    if (offer.sdp) offer.sdp = preferOpusStereo(offer.sdp);
    await pc.setLocalDescription(offer);
    sig.send({ type: 'OFFER', sdp: offer.sdp ?? '' });
  }, [headphoneId, clearGiveUp, giveUp]);

  // Wrapper mit Überlapp-Schutz: nie zwei PC-Aufbauten gleichzeitig (das erzeugte unter
  // wildem Reconnect-Flapping verwaiste PCs + Offer/Answer-Mismatch → Verkeilung). Wird
  // ein Aufbau angefordert während einer läuft, ignorieren — der Reconnect-Loop (pc
  // disconnected → reconnectNow → RESUMED) triggert ohnehin erneut.
  const buildPeer = useCallback(async () => {
    if (rebuildingRef.current) return;
    rebuildingRef.current = true;
    try { await doBuildPeer(); }
    catch { /* ignore */ }
    finally { rebuildingRef.current = false; }
  }, [doBuildPeer]);

  // Durch ADMITTED ausgelöst: Backup-Recorder + Encoder starten (laufen über Reconnects
  // hinweg durch), dann die erste PeerConnection bauen. Der Encoder sendet über dcRef
  // (nicht eine captured dc), damit buildPeer die PC beim Reconnect austauschen kann.
  const goLive = useCallback(async () => {
    const sig = sigRef.current;
    if (!sig) return;
    setStatus('connecting');
    setPacketsSent(0);
    try {
      await startMic(); // läuft i.d.R. schon ab Connect → no-op; sonst Sicherheits-Start
      const rec = await createOpfsRecorder(`speaker-${Date.now()}.pcm`);
      pendingRecorderRef.current = rec; // wird beim Seq-0-Paket aktiv (siehe startMic)
      encoderRef.current?.resetSeq();   // Seq → 0 → Backup beginnt bei Seq 0 (Conform)
      preRollRef.current.length = 0;    // Pre-Roll-Ring leeren: er soll nur Post-Reset-Seqs (ab 0) führen
      sig.setLive(true);
      await buildPeer();
      // Video-Strang anstoßen (best-effort, separat vom L16-Pfad). Der 5s-Poll unten zieht nach,
      // falls die Konferenz erst NACH dem Speaker-Connect startet.
      void startSpeakerConference();
    } catch (err) {
      console.error(err);
      void pendingRecorderRef.current?.close(); pendingRecorderRef.current = null;
      void recorderRef.current?.close(); recorderRef.current = null;
      setStatus('failed');
    }
  }, [startMic, buildPeer, startSpeakerConference]);

  // Tritt dem Warteraum bei. Mic-Encoder + Keepalive starten HIER (Connect-Klick = User-
  // Geste, Tab aktiv) → robuster Erst-Aufbau gegen Hintergrund-Drosselung + Pegel sofort
  // sichtbar. Aufnahme startet trotzdem erst auf ADMITTED (goLive).
  const start = useCallback(async () => {
    if (!micId) return;
    setStatus('waiting');
    setDownloadUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    keepAliveRef.current?.();
    keepAliveRef.current = startKeepAlive();
    try {
      await startMic(); // Encoder JETZT in der Geste warmlaufen lassen (+ Pegel live)
    } catch (e) {
      console.error('mic start failed', e);
      stopMic();
      keepAliveRef.current?.(); keepAliveRef.current = null;
      setStatus('failed');
      return;
    }
    const sig = new SignalingClient(sessionId, 'sender', {
      onAdmitted: () => { void goLive(); },
      onResumed: () => {
        // Give-up-Sicherheitsnetz: jeder Reconnect-Versuch hat einen laufenden 30s-Timer,
        // damit eine in `connecting` verkeilte PC nicht ewig hängt (sie feuert nie
        // disconnected/failed → ohne diesen Arm würde der Give-up nie starten).
        if (giveUpTimerRef.current === null) giveUpTimerRef.current = setTimeout(giveUp, GIVEUP_WINDOW_MS);
        void buildPeer();
      },
      // Entlassen → zurück in den Warteraum. live=false, sonst könnte ein späterer WS-Blip
      // den Wartenden per restore-admit ungewollt wieder live schalten.
      // Zurück in den Warteraum → auch das Video (SFU) abbauen: der Wartende soll dem Studio keine Cam
      // mehr liefern. Beim Re-Admit baut goLive den Video-Strang wieder auf.
      onDismissed: () => { teardownMedia(); speakerConfRef.current?.teardown(); speakerConfRef.current = null; sigRef.current?.setLive(false); setStatus('waiting'); },
      // Kick ist terminal: Client schliessen (stoppt den Auto-Reconnect) — ohne das würde
      // sich der gekickte Speaker per HELLO{live}/restore-admit als Geist zurückklauen.
      // Kick ist terminal: L16 UND Video (SFU) abbauen — sonst publisht der Video-Strang weiter und die
      // Kachel bleibt beim Host stehen (Audio getrennt, Video sichtbar). Der 5s-Rejoin-Poll läuft nur bei
      // status==='connected', nach 'kicked' also nicht → kein ungewolltes Wiederkommen.
      onKicked: () => { clearGiveUp(); keepAliveRef.current?.(); keepAliveRef.current = null; stopMic(); teardownMedia(); speakerConfRef.current?.teardown(); speakerConfRef.current = null; sigRef.current?.close(); sigRef.current = null; setStatus('kicked'); },
      onTakeStart: (takeId, _projectId, uploadKey, uploadToken) => {
        void (async () => {
          const rec = await createOpfsRecorder(`take-${takeId}.pcm`);
          const at = { takeId, uploadKey, uploadToken, gotFirst: false };
          // Pre-Roll: aktuellen Ring VOR die Live-Pakete schreiben, takeStartSeq ~1s früher setzen.
          // Pakete, die während des createOpfsRecorder-await ankommen, landen im Ring und werden hier
          // einmal (über den Flush) geschrieben — nicht doppelt, da takeRecRef erst danach gesetzt wird.
          // gotFirst=true verhindert, dass der Live-Pfad in onPacket den Seq überschreibt.
          const ring = preRollRef.current;
          if (ring.length > 0) {
            for (const pkt of ring) void rec.write(pkt.samples);
            takeStartSeqRef.current.set(takeId, ring[0].seq);
            at.gotFirst = true;
          }
          // Reihenfolge: erst recRef, dann activeTakeRef — sonst sieht onPacket einen aktiven Take
          // ohne Recorder. Beide synchron am Ende, nach dem Ring-Flush.
          takeRecRef.current = rec;
          activeTakeRef.current = at;
        })();
      },
      onTakeStop: (takeId) => {
        void (async () => {
          const t = activeTakeRef.current, rec = takeRecRef.current;
          activeTakeRef.current = null; takeRecRef.current = null;
          if (!t || t.takeId !== takeId || !rec) { sigRef.current?.takeUploaded(takeId, false); return; }
          const pcm = await rec.exportRaw(); // schliesst die per-Take-Datei (bleibt für Retry auf Disk)
          const ok = await uploadTakeSlice(pcm, t.uploadKey, t.uploadToken);
          sigRef.current?.takeUploaded(takeId, ok, takeStartSeqRef.current.get(takeId));
        })();
      },
      onTakeReupload: (takeId, uploadKey, uploadToken) => {
        void (async () => {
          const pcm = await readOpfsRaw(`take-${takeId}.pcm`); // dieselbe (geschlossene) per-Take-Datei
          if (!pcm) { sigRef.current?.takeUploaded(takeId, false, undefined, 'no local backup'); return; }
          const ok = await uploadTakeSlice(pcm, uploadKey, uploadToken);
          sigRef.current?.takeUploaded(takeId, ok, takeStartSeqRef.current.get(takeId));
        })();
      },
      onRoomFull: () => { setStatus('roomfull'); },
      onAnswer: async (sdp) => {
        const pc = pcRef.current;
        if (!pc) return;
        try { await pc.setRemoteDescription({ type: 'answer', sdp }); }
        catch { return; }
        // Gepufferte Remote-Kandidaten jetzt nachziehen (kamen vor der Remote-Description an).
        const pending = pendingCandsRef.current; pendingCandsRef.current = [];
        for (const c of pending) { try { await pc.addIceCandidate(c); } catch { /* ignore */ } }
      },
      onIce: async (candidate) => {
        const pc = pcRef.current;
        if (!pc) return;
        if (!pc.remoteDescription) { pendingCandsRef.current.push(candidate); return; }
        try { await pc.addIceCandidate(candidate); } catch { /* ignore */ }
      },
    }, name);
    sigRef.current = sig;
    sig.open();
  }, [micId, sessionId, name, goLive, teardownMedia, buildPeer, clearGiveUp, giveUp, startMic, stopMic, uploadTakeSlice]);

  // Voller Austritt: Media + Encoder ab, Signaling zu.
  const stop = useCallback(() => {
    clearGiveUp();
    keepAliveRef.current?.(); keepAliveRef.current = null;
    stopMic();
    teardownMedia();
    sigRef.current?.setLive(false);
    sigRef.current?.close();
    sigRef.current = null;
    speakerConfRef.current?.teardown(); speakerConfRef.current = null; // Video-Strang (separat von L16)
    setStatus('idle');
  }, [teardownMedia, clearGiveUp, stopMic]);

  useEffect(() => () => stop(), [stop]);
  // Video-Konferenz-Strang beim Unmount abbauen (separat vom L16-Pfad).
  useEffect(() => () => { speakerConfRef.current?.teardown(); speakerConfRef.current = null; }, []);
  // Poll: die Konferenz kann erst NACH dem Speaker-Connect starten → alle 5s nachziehen.
  useEffect(() => {
    if (status !== 'connected') return;
    const id = setInterval(() => { if (!speakerConfRef.current) void startSpeakerConference(); }, 5000);
    return () => clearInterval(id);
  }, [status, startSpeakerConference]);

  // Solange das Recording-Backup live ist, vor versehentlichem Schliessen/Reload warnen.
  // Bei ECHTEM Unload (pagehide, !persisted = Tab geschlossen/wegnavigiert) sofort BYE an die
  // DO → Slot wird instant frei (kein 30s-Grace wie bei einem Blip). Hintergrund/Minimieren
  // feuert KEIN pagehide (nur visibilitychange) → die Background-Survival + 30s-Grace bei
  // echten Netz-Blips bleiben unangetastet.
  useEffect(() => {
    const recordingLive = status === 'connecting' || status === 'connected' || status === 'reconnecting';
    if (!recordingLive) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    const onPageHide = (e: PageTransitionEvent) => { if (!e.persisted) { sigRef.current?.byeBeacon(); speakerConfRef.current?.byeBeacon(); } };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [status]);

  if (status === 'kicked') return (
    <CustomerPage studio={slug}><CardTitle>Session beendet</CardTitle><Hint>Diese Session ist beendet. Du kannst das Fenster schließen.</Hint></CustomerPage>
  );
  if (status === 'roomfull') return (
    <CustomerPage studio={slug}><CardTitle>Warteraum voll</CardTitle><Hint>Der Warteraum der Session ist voll, bitte später erneut versuchen.</Hint></CustomerPage>
  );
  if (status === 'lost') return (
    <CustomerPage studio={slug}>
      <CardTitle>Verbindung verloren</CardTitle>
      <Hint>Die Verbindung konnte nicht wiederhergestellt werden. Dein lokales Backup ist gesichert.</Hint>
      {downloadUrl && <Row><a href={downloadUrl} download="speaker-recording.wav">Download local recording (.wav)</a></Row>}
      <Pill primary disabled={!micId} onClick={start}>Erneut verbinden</Pill>
    </CustomerPage>
  );

  // Live (connected/reconnecting) → geteilte 3-Spalten-Shell
  if (status === 'connected' || status === 'reconnecting') return (
    <LiveShell
      studio={slug}
      status="recording"
      left={
        <>
          <ColLabel>Voice</ColLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
            {name && <div style={{ fontWeight: 'var(--weight-medium)' }}>{name}</div>}
            <div>Mic level<br /><LevelMeter samplesProvider={() => lastSamplesRef.current} /></div>
            <div style={{ fontSize: 'var(--text-p-small)' }}>Connection: <strong style={{ color: status === 'reconnecting' ? 'var(--color-danger)' : 'var(--color-success)' }}>{status === 'reconnecting' ? 'verloren — neu verbinden…' : 'Good'}</strong></div>
            <div style={{ fontSize: 'var(--text-p-small)' }}>Return: <strong>{hasReturn ? 'receiving' : '—'}</strong></div>
            <div style={{ fontSize: 'var(--text-label)', opacity: 0.5 }}>Packets sent: {packetsSent}</div>
            <Pill onClick={stop}>Disconnect</Pill>
            {downloadUrl && <a href={downloadUrl} download="speaker-recording.wav">Backup (.wav)</a>}
          </div>
        </>
      }
      center={<><ColLabel>Video</ColLabel><VideoStage track={hostVideoTrack} /></>}
      right={
        <>
          <ColLabel>Conference</ColLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-xs)' }}>
            {selfCamTrack && <CamTile track={selfCamTrack} label="Du" />}
            {confSpeakers.map((s) => <CamTile key={`sp:${s.speakerId}`} track={peerVideos.get(`sp:${s.speakerId}`) ?? null} label={s.name} />)}
            {confGuests.map((g) => <CamTile key={`g:${g.guestId}`} track={peerVideos.get(`g:${g.guestId}`) ?? null} label={g.name} />)}
          </div>
        </>
      }
    />
  );

  // Warteraum / Verbindungsaufbau
  if (status === 'waiting' || status === 'connecting') return (
    <CustomerPage studio={slug} status="on-air">
      <CardTitle>Voice</CardTitle>
      {name && <Hint>{name}</Hint>}
      <Hint>{status === 'connecting' ? 'Verbinde…' : 'Im Warteraum — warte auf Freigabe durch TRO…'}</Hint>
      <Pill onClick={stop}>Cancel</Pill>
    </CustomerPage>
  );

  // idle / failed → Geräte-Setup (VOICE)
  return (
    <CustomerPage studio={slug}>
      <CardTitle>Voice</CardTitle>
      <PermissionNotice ready={media.ready} camReady={media.camReady} requesting={media.requesting} onRequest={() => void media.request()} />
      <Field label="Dein Name"><TextInput value={name} onChange={(e) => setName(e.target.value)} disabled={status !== 'idle'} maxLength={40} placeholder="Name" /></Field>
      <Field label="Mikrofon"><Select value={micId} onChange={(e) => { setMicId(e.target.value); localStorage.setItem(`tro-spk-mic:${slug}`, e.target.value); }}>
        <option value="">— Gerät wählen —</option>{inputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>)}
      </Select></Field>
      <Row><span style={{ fontSize: 'var(--text-label)', textTransform: 'uppercase', letterSpacing: 'var(--ls-label)', opacity: 0.6 }}>Signal</span> <InputDeviceMeter deviceId={micId} /></Row>
      <Field label="Kopfhörer (Return / Talkback)"><Select value={headphoneId} onChange={(e) => { setHeadphoneId(e.target.value); localStorage.setItem(`tro-spk-out:${slug}`, e.target.value); }}>
        <option value="">Standard</option>{outputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>)}
      </Select></Field>
      <Field label="Webcam">
        <select value={camId} disabled={status !== 'idle'} onChange={(e) => { setCamId(e.target.value); localStorage.setItem(`tro-spk-cam:${slug}`, e.target.value); }}>
          <option value="">No Video</option>{cams.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>)}
        </select>
      </Field>
      {camId && <Row><CamPreview deviceId={camId} /></Row>}
      {status === 'failed' && <Hint tone="error">Verbindung fehlgeschlagen — bitte erneut versuchen.</Hint>}
      <Pill primary disabled={!micId || status !== 'idle'} onClick={start}>Connect</Pill>
      {downloadUrl && <Row><a href={downloadUrl} download="speaker-recording.wav">Download local recording (.wav)</a></Row>}
    </CustomerPage>
  );
}
